import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MapPin, QrCode, PenLine, Loader2 } from 'lucide-react';
import { queueOfflineAction, ACTION_TYPES } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';
import { getCurrentPosition } from '@/lib/mobile/geo';
import { clockInVariance, clockOutVariance } from '@/lib/clockVariance';
import { ClockVarianceReason } from '@/components/shared/ClockVarianceReason';

/**
 * Compliance-friendly check-in / check-out dialog (Day 14).
 * Modes: GPS (default), QR fallback, Manual (mandatory reason).
 * Captures a one-shot GPS fix, asks for the early/late reason outside the grace window, and queues the action via
 * the existing offline action queue (CHECKIN / CHECKOUT).
 */
export default function MobileCheckInDialog({ open, onOpenChange, mode = 'in', shift, onDone }) {
  const isOut = mode === 'out';
  const [method, setMethod] = useState('GPS');
  const [reason, setReason] = useState('');
  const [qrToken, setQrToken] = useState('');
  const [gps, setGps] = useState(null);          // {lat,lng,accuracy}
  const [gpsState, setGpsState] = useState('idle'); // idle|locating|ok|error
  const [submitting, setSubmitting] = useState(false);

  const [varianceReason, setVarianceReason] = useState('');
  // Early/late vs the scheduled start (check-in) or end (check-out), beyond the grace window -> reason required
  const variance = open && shift ? (isOut ? clockOutVariance(shift) : clockInVariance(shift)) : null;
  const varianceBlocked = !!variance && !varianceReason.trim();

  const captureGps = useCallback(async () => {
    setGpsState('locating');
    const res = await getCurrentPosition();
    if (res.ok) { setGps(res); setGpsState('ok'); }
    else { setGps(null); setGpsState('error'); }
  }, []);

  useEffect(() => {
    if (open) {
      setMethod('GPS'); setReason(''); setVarianceReason(''); setQrToken(''); setGps(null); setGpsState('idle');
      captureGps();
    }
  }, [open, captureGps]);

  const submit = async () => {
    if (method === 'MANUAL' && !reason.trim()) {
      toast.error('Please enter a reason for the manual ' + (isOut ? 'check-out' : 'check-in'));
      return;
    }
    if (method === 'QR' && !qrToken.trim()) {
      toast.error('Please scan or enter the site QR code');
      return;
    }
    if (varianceBlocked) {
      toast.error('Please enter the reason for the early/late ' + (isOut ? 'check-out' : 'check-in'));
      return;
    }
    setSubmitting(true);
    try {
      const online = isOnline();
      const payload = {
        shiftId: shift.id,
        timestamp: new Date().toISOString(),
        method,
        manualReason: method === 'MANUAL' ? reason.trim() : undefined,
        qrToken: method === 'QR' ? qrToken.trim() : undefined,
        varianceReason: variance ? varianceReason.trim() : undefined,
        lat: method === 'MANUAL' ? undefined : gps?.lat,
        lng: method === 'MANUAL' ? undefined : gps?.lng,
        accuracy: method === 'MANUAL' ? undefined : gps?.accuracy,
      };
      await queueOfflineAction(isOut ? ACTION_TYPES.CHECKOUT : ACTION_TYPES.CHECKIN, payload);
      if (online) {
        const res = await syncAllActions();
        if (res.error) {
          toast.error('Saved offline — will retry. ' + res.error);
        } else {
          toast.success(isOut ? 'Checked out' : 'Checked in');
        }
      } else {
        toast.success((isOut ? 'Check-out' : 'Check-in') + ' queued — will sync when online');
      }
      onOpenChange(false);
      onDone && onDone();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle>{isOut ? 'Check out of shift' : 'Check in to shift'}</DialogTitle>
          <DialogDescription className="text-xs">
            Choose how to confirm your {isOut ? 'check-out' : 'check-in'}. GPS is captured for compliance; use QR or Manual if location is unavailable.
          </DialogDescription>
        </DialogHeader>

        <ClockVarianceReason action={isOut ? 'out' : 'in'} variance={variance} value={varianceReason} onChange={setVarianceReason} />

        <Tabs value={method} onValueChange={setMethod} className="w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="GPS" data-testid="method-gps"><MapPin className="h-4 w-4 mr-1" /> GPS</TabsTrigger>
            <TabsTrigger value="QR" data-testid="method-qr"><QrCode className="h-4 w-4 mr-1" /> QR</TabsTrigger>
            <TabsTrigger value="MANUAL" data-testid="method-manual"><PenLine className="h-4 w-4 mr-1" /> Manual</TabsTrigger>
          </TabsList>

          <TabsContent value="GPS" className="pt-3">
            <div className="text-sm space-y-2" data-testid="gps-panel">
              {gpsState === 'locating' && <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Getting your location…</p>}
              {gpsState === 'ok' && (
                <p className="text-green-600 flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Location captured
                  {gps?.accuracy ? <Badge variant="secondary" className="text-[10px]">GPS accuracy ±{Math.round(gps.accuracy)} m</Badge> : null}
                </p>
              )}
              {gpsState === 'error' && (
                <div className="space-y-2">
                  <p className="text-amber-600 text-xs">Couldn't get a location fix. Retry, or use QR / Manual instead.</p>
                  <Button size="sm" variant="outline" onClick={captureGps} data-testid="gps-retry">Retry location</Button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="QR" className="pt-3">
            <div className="space-y-2" data-testid="qr-panel">
              <Label htmlFor="qr-input" className="text-xs">Site QR code</Label>
              <Input id="qr-input" value={qrToken} onChange={(e) => setQrToken(e.target.value)} placeholder="Scan or enter the site code" data-testid="qr-input" />
              <p className="text-[11px] text-muted-foreground">Use this when GPS is unavailable. Scan the QR posted at the site.</p>
            </div>
          </TabsContent>

          <TabsContent value="MANUAL" className="pt-3">
            <div className="space-y-2" data-testid="manual-panel">
              <Label htmlFor="manual-reason" className="text-xs">Reason (required)</Label>
              <Textarea id="manual-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={`Why are you checking ${isOut ? 'out' : 'in'} manually?`} rows={3} data-testid="manual-reason-input" />
              <p className="text-[11px] text-muted-foreground">Manual {isOut ? 'check-out' : 'check-in'} is recorded as an exception for review.</p>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="checkin-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting || varianceBlocked} data-testid="checkin-confirm">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {isOut ? 'Confirm check-out' : 'Confirm check-in'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
